import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setCachedAnimationDetails } from "../../../app/slices/AnimationsSlice";
import PiButton from "../../../components/PiButton";
import {
  getAnimateActorAnimationId,
  getAnimateActorAnimationName,
  getAnimateActorAnimationState,
  IAnimateActor,
  setAnimateActorLayer,
} from "../../../models/AnimateActor";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import Interaction from "../../../models/interactions/Interaction";
import AnimationLibrary from "../../previews/animation/AnimationLibrary";
import {
  ANIMATION_UNAVAILABLE_LABEL,
  getAnimationDisplayLabel,
} from "../../previews/animation/animationDisplay";
import useAnimateActorAnimationDetails from "../../previews/animation/useAnimateActorAnimationDetails.hook";
import { useAnimationRender } from "../../previews/animation/useAnimationAssets.hook";
import NodeInfoField from "../components/NodeInfoField";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeActorDropdown from "../components/NodeActorDropdown";
import PiInputfield from "../../../components/PiInputField";
import NodeToggle from "../components/NodeToggle";
export default function AnimateActorNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as IAnimateActor;
  const animationName = getAnimateActorAnimationName(interaction.animation);
  const animationState = getAnimateActorAnimationState(interaction.animation);
  const animationId = getAnimateActorAnimationId(interaction.animation);
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const { details: animationDetails, isUnavailable } =
    useAnimateActorAnimationDetails(props.interaction);
  const { renderUri } = useAnimationRender(animationId || undefined);

  const [expanded, setExpanded] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  const actorDropdown = useMemo(
    () => (
      <NodeActorDropdown
        label="Actor"
        selectedActorId={interaction.npcId}
        onClick={(actorId) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            npcId: actorId,
          } as IAnimateActor)
        }
      />
    ),
    [interaction.npcId, updateInteraction, props.nodeId],
  );

  const animationInfoField = useMemo(
    () => (
      <NodeInfoField
        label="Animation"
        value={
          animationName
            ? getAnimationDisplayLabel(animationDetails ?? {
                name: animationName,
                animationState,
              })
            : "No animation selected"
        }
      />
    ),
    [animationDetails, animationName, animationState],
  );

  const layerInputfield = useMemo(
    () => (
      <PiInputfield
        label="Layer"
        placeholder="Layer"
        value={interaction.layer.toString()}
        onChange={(layer: string) =>
          updateInteraction(
            props.nodeId,
            setAnimateActorLayer(interaction, Number(layer))
          )
        }
      />
    ),
    [interaction.layer, updateInteraction, props.nodeId],
  );

  const transitionTimeInputfield = useMemo(
    () => (
      <PiInputfield
        label="Transition Time"
        placeholder="Transition Time"
        value={interaction.transitionTime.toString()}
        onChange={(transitionTime: string) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            transitionTime: Number(transitionTime),
          } as IAnimateActor)
        }
      />
    ),
    [interaction.transitionTime, updateInteraction, props.nodeId],
  );

  const waitUntilFinishedToggle = useMemo(
    () => (
      <NodeToggle
        label="Wait"
        value={interaction.waitUntilFinished}
        onChange={(waitUntilFinished) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            waitUntilFinished,
          } as IAnimateActor)
        }
      />
    ),
    [interaction.waitUntilFinished, updateInteraction, props.nodeId],
  );

  const onSelectAnimation = (animationDetails: AnimationDetailsDTO) => {
    updateInteraction(props.nodeId, {
      ...interaction,
      animation: {
        name: animationDetails.name,
        animationState: animationDetails.animationState,
        animationId: animationDetails.externalId,
      },
      layer: animationDetails.layer,
    } as IAnimateActor);

    dispatch(
      setCachedAnimationDetails({
        externalId: animationDetails.externalId,
        animationDetails,
      })
    );
  };

  const animationPreview = animationName && (
    <div className="relative flex h-[120px] w-full items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground">
      {renderUri && !hasImageError ? (
        <>
          {!isImageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-creator-surface" />
          )}
          <img
            className="h-full w-full object-contain p-[8px]"
            src={renderUri}
            alt={animationName}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => {
              setHasImageError(true);
              setIsImageLoaded(false);
            }}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-[4px] text-creator-text">
          <DirectionsWalkOutlinedIcon fontSize="large" color="inherit" />
          {isUnavailable && (
            <div
              className="text-[12px] font-medium"
              data-testid="animation-unavailable"
            >
              {ANIMATION_UNAVAILABLE_LABEL}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const selectAnimationButton = (
    <div className="mt-[12px] h-[36px]">
      <PiButton variant="piBlueOutlined" onClick={() => setIsLibraryOpen(true)}>
        {animationName ? "Change Animation" : "Select Animation"}
      </PiButton>
    </div>
  );

  const expandButton = useMemo(
    () => (
      <div
        className="flex flex-row gap-2 w-full h-full text-[24px] items-center justify-center text-creator-primary cursor-pointer hover:text-creator-primary-hover"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <KeyboardDoubleArrowUpIcon fontSize="inherit" color="inherit" />
        ) : (
          <KeyboardDoubleArrowDownIcon fontSize="inherit" color="inherit" />
        )}
      </div>
    ),
    [expanded],
  );

  const expandedContent = (
    <motion.div
      className="flex flex-col gap-[12px] overflow-hidden h-full"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "190px", transition: { duration: 0.1 } }}
      exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
    >
      {layerInputfield}
      {transitionTimeInputfield}
      {waitUntilFinishedToggle}
    </motion.div>
  );

  return (
    <div className="flex flex-col gap-[12px] w-full h-full">
      {actorDropdown}
      {animationInfoField}
      {animationPreview}
      {selectAnimationButton}
      <AnimatePresence initial={false}>
        {expanded && expandedContent}
      </AnimatePresence>
      {expandButton}
      {isLibraryOpen && (
        <AnimationLibrary
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          title="Animation Library"
          width="calc(90vw / var(--app-zoom, 1))"
          height="calc(90vh / var(--app-zoom, 1))"
          onSubmit={onSelectAnimation}
        />
      )}
    </div>
  );
}
